import React, { useState, useEffect } from "react";
import "./RadioBlocksExtended.css";
import { toEuro, toEuroRound } from "src/helpers/calculation";
import { usePopup } from "src/contexts/PopupContext";

type RadioBlockProps = {
  block: RadioBlockInterface;
  isSelected: boolean;
  isDisabled: boolean;
  parentIsSelected?: boolean;
  onClick: (value: string) => void;
};

interface RadioBlockInterface {
  id: number;
  name: string;
  imageUrl?: string;
  color?: string;
  value: string;
  price?: number;
  description?: string;
  component?: React.FC;
  layout?: string; // after or in_value
  selectedNames?: string;
  carouselImages?: string[];
}

interface RadioBlocksExtendedProps {
  blocks: RadioBlockInterface[];
  label?: string;
  value: string;
  disableValues?: string[];
  className?: string;
  parentIsSelected?: boolean;
  onChange: (selectedValue: string) => void;
}

const ArrowIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M16 12L10 6L8.59 7.41L13.17 12L8.59 16.59L10 18L16 12Z"
      fill="currentColor"
    />
  </svg>
);

const RadioBlock: React.FC<RadioBlockProps> = ({
  block,
  isSelected,
  isDisabled,
  parentIsSelected,
  onClick,
}) => {
  const [showDetails, setShowDetails] = React.useState(false);
  const [showComponent, setShowComponent] = React.useState(false);

  const { setIsCarouselVisible, setCarouselImages } = usePopup();

  const highlight =
    parentIsSelected !== undefined
      ? parentIsSelected && isSelected
      : isSelected;
  useEffect(() => {
    setShowComponent(isSelected);
    // console.log(block.value, isSelected, showComponent);
  }, [isSelected]);

  if (!block.color && !block.imageUrl) block.color = "#9BFFBE";
  return (
    <>
      <div
        key={block.id}
        className={`radio-eblock ${highlight ? "selected" : ""} ${
          highlight && (!showComponent || !block.component) ? "selected_bg" : ""
        }${isDisabled ? "disabled" : ""} ${
          block.layout === "in_value" ? "in_value" : ""
        }`}
        data-value={block.value}
        onClick={(event) => {
          // if (parentIsSelected === undefined || parentIsSelected)
          //   event.stopPropagation();

          if (block.component && !showComponent) {
            event.stopPropagation();
          }
          if (!isDisabled) {
            if (isSelected) setShowComponent(!showComponent);
            // else setShowComponent(true);
          }
          if (
            event.target instanceof HTMLElement &&
            (event.target.classList.contains("simple-number-input__minus") ||
              event.target.classList.contains("simple-number-input__plus"))
          ) {
            return;
          }
          return !isDisabled && onClick(block.value);
        }}
      >
        <div className="radio-eblock-content">
          {/* <div className="common-option-check"></div> */}
          <div
            className={`icon${
              (block.imageUrl && !block.imageUrl.includes("empty")) ||
              block.carouselImages
                ? " cursor"
                : ""
            }`}
            style={{ backgroundColor: block.color }}
            onClick={(event) => {
              if (block.carouselImages) {
                event.stopPropagation();

                setCarouselImages(block.carouselImages);
                setIsCarouselVisible(true);
              } else if (block.imageUrl && !block.imageUrl.includes("empty")) {
                event.stopPropagation();

                setCarouselImages([block.imageUrl]);
                setIsCarouselVisible(true);
              }
            }}
          >
            {block.imageUrl && <img src={block.imageUrl} alt={block.name} />}
          </div>
          <div className="details_container">
            <div className="details_text">
              <div className={`name ${highlight ? "selected" : ""}`}>
                {block.name}
              </div>
              {block.price ? (
                <div className="price">
                  {block.price > 0 ? toEuroRound(block.price) : ""}
                </div>
              ) : (
                <></>
              )}
            </div>
            <div className="details_childs">
              <span>{highlight ? block.selectedNames : null}</span>
              <div className="details_buttons">
                {block.description && (
                  <div
                    className={`details ${showDetails ? "open" : ""}`}
                    onClick={(event) => {
                      setShowDetails(!showDetails);
                      event.stopPropagation();
                    }}
                  >
                    <span>Details</span>
                  </div>
                )}
                {block.component ? (
                  <div
                    className={`details_icon ${showComponent ? "open" : ""}`}
                    onClick={(event) => {
                      setShowComponent(!showComponent);
                      event.stopPropagation();
                    }}
                  >
                    <ArrowIcon />
                  </div>
                ) : (
                  false
                )}
              </div>
            </div>
          </div>
        </div>
        {block.description && showDetails && (
          <div className="description">{block.description}</div>
        )}
        {block.component && showComponent && (
          <div className="value">
            <div className="component">{<block.component />}</div>
          </div>
        )}
      </div>
    </>
  );
};

const RadioBlocksExtended: React.FC<RadioBlocksExtendedProps> = ({
  blocks,
  label,
  value,
  disableValues = [],
  className,
  parentIsSelected,
  onChange,
}) => {
  const [selectedValue, setSelectedValue] = useState(value);

  useEffect(() => {
    setSelectedValue(value);
    // setShowComponent(false);
  }, [value]);

  const handleBlockClick = (newValue: string) => {
    if (!disableValues.includes(newValue)) {
      setSelectedValue(newValue);
      onChange(newValue);
    }
  };

  return (
    <div className={`radio-eblocks ${className || ""}`}>
      {label && <div className="radio-eblocks-label">{label}</div>}
      {blocks.map((block) => (
        <RadioBlock
          key={block.id}
          block={block}
          isSelected={block.value === selectedValue}
          isDisabled={disableValues.includes(block.value)}
          onClick={handleBlockClick}
          parentIsSelected={parentIsSelected}
        />
      ))}
    </div>
  );
};

export default RadioBlocksExtended;
